/**
 * Trailer–cargo validation context for supply flow: same route + trailers-per-edge as TripDetailScreen,
 * plus mapping from operation DAG node indices to trip DAG indices via shared logical node ids.
 */

import { inferRouteStructure, type RouteStructure } from './routeInference';
import { buildIncompatibilityKeySet, buildTrailersPerEdge, incompatibilityPairsFromRows } from './trailerCargoBan';
import type { DagNodeRow } from './tripLpRequirements';
import type { TripForIssuesCount } from './tripIssuesCount';

function parseEventDate(value: string | number | null | undefined): Date | null {
    if (value == null || value === '') return null;
    const d = new Date(value as string | number);
    return Number.isNaN(d.getTime()) ? null : d;
}

type TrailerIncompatibilityRow = {
    trailerItemType?: { id?: string } | null;
    cargoItemType?: { id?: string } | null;
};

/** DB trip DAG rows include storage ids; `DagNodeRow` omits them but runtime has them. */
type DagNodeRowWithStorage = DagNodeRow & { storageLocationId?: string | null };

export type TripTrailerSupplyFlowContext = {
    routeStructure: RouteStructure | null;
    trailersPerEdge: Map<string, Set<string>> | null;
    incompatibilityKeys: Set<string>;
    /** Logical node id (`storage:source:…`, `eventId`, `storage:dest:…`) → trip DAG node index */
    logicalIdToTripIndex: Map<string, number>;
};

function buildLogicalIdToTripIndex(dagNodes: DagNodeRowWithStorage[]): Map<string, number> {
    const m = new Map<string, number>();
    for (const n of dagNodes) {
        let id: string;
        if (n.kind === 'source') id = `storage:source:${n.storageLocationId ?? ''}`;
        else if (n.kind === 'dest') id = `storage:dest:${n.storageLocationId ?? ''}`;
        else id = n.eventId ?? '';
        m.set(id, n.index);
    }
    return m;
}

/**
 * Builds route structure, trailers-per-edge, incompatibility keys, and logical id → trip index map
 * for the same validation as trip supply flow. Returns null if the trip has no usable DAG.
 */
export function buildTripTrailerSupplyFlowContext(
    trip: TripForIssuesCount | null | undefined,
    trailerCargoIncompatibilityRows: TrailerIncompatibilityRow[] | null | undefined
): TripTrailerSupplyFlowContext | null {
    if (!trip?.tripDagNodes?.length) return null;

    const eventsRaw = trip.events ?? [];
    const events = eventsRaw.slice().sort((a, b) => {
        const aStart = parseEventDate(a.startDate)?.getTime() ?? 0;
        const bStart = parseEventDate(b.startDate)?.getTime() ?? 0;
        return aStart - bStart;
    });

    const eventsMap = new Map<string, { id: string; name?: string; startDate?: Date | null; endDate?: Date | null }>();
    for (const e of events) {
        eventsMap.set(e.id, {
            id: e.id,
            name: e.name,
            startDate: parseEventDate(e.startDate),
            endDate: parseEventDate(e.endDate),
        });
    }

    const packingListsForInference = new Map<string, Array<{ itemType?: { id: string; category?: { name: string } | null } | null }>>();
    for (const e of events) {
        const items = (e.packingListItems ?? []).map(item => ({
            itemType: item.itemType
                ? { id: item.itemType.id, category: item.itemType.category ? { name: item.itemType.category.name } : null }
                : null,
        }));
        if (items.length > 0) packingListsForInference.set(e.id, items);
    }

    const sourceStorageLocationId = trip.sourceStorageLocation?.id ?? null;
    const destStorageLocationId = trip.destStorageLocation?.id ?? null;

    const routeStructure =
        events.length > 0
            ? inferRouteStructure(
                  [],
                  events.map(e => e.id),
                  eventsMap,
                  packingListsForInference,
                  sourceStorageLocationId ? [sourceStorageLocationId] : [],
                  destStorageLocationId ? [destStorageLocationId] : []
              )
            : null;

    const dagNodesFromTrip = (
        (trip.tripDagNodes ?? []) as Array<{
            index: number;
            kind: 'source' | 'event' | 'dest';
            eventId?: string | null;
            storageLocationId?: string | null;
        }>
    )
        .slice()
        .sort((a, b) => a.index - b.index) as DagNodeRowWithStorage[];

    const incompatibilityKeys = buildIncompatibilityKeySet(incompatibilityPairsFromRows(trailerCargoIncompatibilityRows));

    const trailersPerEdge =
        routeStructure && sourceStorageLocationId && destStorageLocationId && dagNodesFromTrip.length > 0
            ? buildTrailersPerEdge(routeStructure, dagNodesFromTrip, sourceStorageLocationId, destStorageLocationId)
            : null;

    const logicalIdToTripIndex = buildLogicalIdToTripIndex(dagNodesFromTrip);

    return {
        routeStructure,
        trailersPerEdge,
        incompatibilityKeys,
        logicalIdToTripIndex,
    };
}
